import { create } from "zustand";
import { api, uploadFile } from "../utils/api";
import { ApiError } from "../utils/api";
import type { RunSummary, RunDetail, TrackWeek } from "../types";

interface RunState {
  runs: RunSummary[];
  trackWeeks: TrackWeek[];
  _cachedTeamId: string | null;
  selectedTrackWeekId: string | null;
  // Per-week selected car for the Fuel/Pit switcher (#279) — shared so a car
  // chosen on Fuel carries to Pit within the same week, reset per week.
  selectedCarByWeek: Record<string, string>;
  loading: boolean;
  // Distinguishes "the request failed" from "there are genuinely no runs" so
  // boards don't render a confident empty state over a network error.
  error: string | null;
  uploading: boolean;
  uploadProgress: number;

  fetchTrackWeeks: (teamId: string, seriesName?: string) => Promise<void>;
  setTrackWeek: (id: string) => void;
  setSelectedCar: (trackWeekId: string, carId: string) => void;
  fetchRuns: (teamId: string, trackWeekId?: string) => Promise<void>;
  fetchRunDetail: (runId: string) => Promise<RunDetail>;
  uploadIbt: (file: File, teamId: string) => Promise<string>;
  deleteRun: (runId: string) => Promise<void>;
  pollJobStatus: (jobId: string) => Promise<{ status: string; runId?: string; error?: string }>;
}

export const useRunStore = create<RunState>((set, get) => ({
  runs: [],
  trackWeeks: [],
  _cachedTeamId: null,
  selectedTrackWeekId: null,
  selectedCarByWeek: {},
  loading: false,
  error: null,
  uploading: false,
  uploadProgress: 0,

  fetchTrackWeeks: async (teamId: string, seriesName?: string) => {
    try {
      // Clear cache when switching teams
      if (get()._cachedTeamId !== teamId) {
        set({ trackWeeks: [], selectedTrackWeekId: null, _cachedTeamId: teamId });
      }
      let url = `/api/track-weeks?team_id=${teamId}`;
      if (seriesName) url += `&series_name=${encodeURIComponent(seriesName)}`;
      const weeks = await api<TrackWeek[]>(url);
      const currentSelectedId = get().selectedTrackWeekId;
      const preserved = currentSelectedId
        ? weeks.find((w) => w.id === currentSelectedId)
        : null;
      const active = preserved || weeks.find((w) => w.isActive) || weeks[0];
      set({
        trackWeeks: weeks,
        selectedTrackWeekId: active?.id || null,
        _cachedTeamId: teamId,
      });
    } catch { /* ignore */ }
  },

  setTrackWeek: (id: string) => {
    set({ selectedTrackWeekId: id });
  },

  setSelectedCar: (trackWeekId: string, carId: string) => {
    set((s) => ({ selectedCarByWeek: { ...s.selectedCarByWeek, [trackWeekId]: carId } }));
  },

  fetchRuns: async (teamId: string, trackWeekId?: string) => {
    set({ loading: true, error: null });
    try {
      let url = `/api/runs?team_id=${teamId}`;
      if (trackWeekId) url += `&track_week_id=${trackWeekId}`;
      const runs = await api<RunSummary[]>(url);
      set({ runs, loading: false });
    } catch {
      set({ loading: false, error: "Couldn't load runs. Check your connection and try again." });
    }
  },

  fetchRunDetail: async (runId: string) => {
    return api<RunDetail>(`/api/runs/${runId}`);
  },

  uploadIbt: async (file: File, teamId: string) => {
    set({ uploading: true, uploadProgress: 0 });
    try {
      const result = await uploadFile("/api/upload", file, { team_id: teamId }, (pct) => {
        set({ uploadProgress: pct });
      });
      set({ uploading: false, uploadProgress: 100 });
      return result.jobId;
    } catch (err) {
      set({ uploading: false, uploadProgress: 0 });
      throw err;
    }
  },

  deleteRun: async (runId: string) => {
    await api(`/api/runs/${runId}`, { method: "DELETE" });
    set((s) => ({ runs: s.runs.filter((r) => r.id !== runId) }));
  },

  pollJobStatus: async (jobId: string) => {
    const poll = async (
      missingRetries = 0
    ): Promise<{ status: string; runId?: string; error?: string }> => {
      let result: { status: string; runId?: string; error?: string };
      try {
        result = await api<{ status: string; runId?: string; error?: string }>(`/api/jobs/${jobId}`);
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.status === 404 &&
          missingRetries < 4
        ) {
          await new Promise((r) => setTimeout(r, 400));
          return poll(missingRetries + 1);
        }
        throw err;
      }

      if (result.status === "complete") {
        return result;
      }
      if (result.status === "failed") {
        throw new Error(result.error || "Telemetry processing failed");
      }
      await new Promise((r) => setTimeout(r, 2000));
      return poll(0);
    };
    return poll();
  },
}));
